// BacklitError is the single error class every SDK method throws.
// Callers discriminate on `.code` (matches the server's
// {error:{code,message}} envelope verbatim) and optionally `.status` (HTTP
// status; 0 for network errors or client-side validation).
//
// Why one class instead of a hierarchy: callers write
// `try {...} catch (e) { if (e instanceof BacklitError && e.code === "x") ...}`.
// Splitting into NetworkError / ServerError / ValidationError would force
// every caller to write three catches or use a base — net negative.

// ServerErrorCode is GENERATED from internal/errcodes.All (the Go single source
// of truth for the wire `code` strings) by scripts/gen-sdk-codes; CI gates the
// generated file with git diff --exit-code so the Go emitters and this union can
// never drift (the cross-boundary-audit handoff_*/invalid_glow_state finding).
// Note: "conflict" and "invalid_argument" appear here because the server CAN
// emit them (records 412/400); they are ALSO produced client-side below.
import type { ServerErrorCode } from "./server-codes.gen.js";

// ClientErrorCode is the hand-maintained set of codes the SDK raises itself —
// the server never emits these, so they are deliberately NOT in errcodes.All.
export type ClientErrorCode =
  // A network/transport failure or client-side pre-flight validation (status 0).
  | "network"
  // updateJSON exhausted its compare-and-swap retries under contention.
  | "conflict"
  // A client-side bad-argument (e.g. a non-JSON-encodable putJSON value).
  | "invalid_argument"
  // The SDK is running off a recognized backlit host (an AI design-tool
  // iframe, a file:// page, …), so there is no data-API to reach; every
  // data/userdata/records/capture op throws this rather than mis-targeting
  // fetch. auth.* stay never-throwing (they resolve null off-host). push.*
  // subscribe/state/etc. also throw this off-host (push.supported() reports
  // it as { supported:false } instead of throwing — the one sync method).
  // backlit.device.* throws it off-host too.
  | "unsupported_host"
  // push.subscribe() ran in a context where push is not available at all —
  // no service worker / PushManager / secure context (push.supported() is
  // false with a `reason`). Distinct from unsupported_host (that is "off a
  // glow host"); this is "on a glow host, but the browser can't do push".
  // backlit.device.* throws it too when there is no on-device storage it can
  // open (no IndexedDB, an open failure, or a newer build's schema).
  | "unsupported"
  // push.subscribe() ran on a platform where push works only after the app
  // is installed to the home screen and it is NOT yet installed (iOS Safari:
  // push requires an installed PWA). push.supported() reports this as
  // { requiresInstall:true, installed:false } ahead of the throw.
  | "requires_install"
  // push.subscribe() called Notification.requestPermission() and the user
  // denied (or had previously denied) notification permission.
  | "permission_denied";

// BacklitErrorCode is every code a caller can observe: server-emitted (matches
// the {error:{code,message}} envelope verbatim) plus client-side. "conflict"
// and "invalid_argument" are intentionally in both halves — same string, two
// origins — and TypeScript dedups them in the union.
export type BacklitErrorCode = ServerErrorCode | ClientErrorCode;

export class BacklitError extends Error {
  readonly code: BacklitErrorCode;
  readonly status: number;
  // ES2022 added `cause` to Error itself, but our target (ES2020) doesn't
  // know about it. Carry it as a non-`override` field so the lib level
  // doesn't matter — assignment is just a regular property write.
  readonly backlitCause: unknown;

  constructor(code: BacklitErrorCode, message: string, status = 0, cause?: unknown) {
    super(message);
    this.name = "BacklitError";
    this.code = code;
    this.status = status;
    this.backlitCause = cause;
    // Make instanceof reliable across realms (rare in practice, cheap here).
    Object.setPrototypeOf(this, BacklitError.prototype);
  }
}
